import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  StatCard,
  ContentCard,
  DashboardHeader,
} from "../common/DashboardComponents";
import Loader from "../common/Loader";
import {
  getAllUserList,
  getAllUserRole,
  handleDeleteUser,
} from "../../redux/actions/grievanceActions1";
import "../../styles/AresaUserList.css";
import AddMember from "../AddMember/AddMember";
import ResetPassword from "../ResetPassword/ResetPassword";

// Modern Icons
const Icons = {
  Users: <i className="bi bi-people-fill"></i>,
  User: <i className="bi bi-person-fill"></i>,
  Filter: <i className="bi bi-funnel-fill"></i>,
  Search: <i className="bi bi-search"></i>,
  Calendar: <i className="bi bi-calendar-date"></i>,
  Mobile: <i className="bi bi-phone"></i>,
  ID: <i className="bi bi-fingerprint"></i>,
  ServiceNumber: <i className="bi bi-card-heading"></i>,
  Status: <i className="bi bi-check-circle"></i>,
  Actions: <i className="bi bi-three-dots-vertical"></i>,
  ViewDetails: <i className="bi bi-eye"></i>,
  FirstPage: <i className="bi bi-chevron-double-left"></i>,
  PrevPage: <i className="bi bi-chevron-left"></i>,
  NextPage: <i className="bi bi-chevron-right"></i>,
  LastPage: <i className="bi bi-chevron-double-right"></i>,
};

const Registration = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { getAllUser, loading, userRoleMaster } = useSelector(
    (state) => state.grievances
  );
  const totalUser = getAllUser.length;

  // Local state
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [userDetails, setUserDetails] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  const openModal = () => {
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
  };
  const handleResetModal = (user) => {
    setSearchTerm("");
    setUserDetails(user);
    setShowResetModal(true);
  };
  const handleEditUser = (user) => {
    setUserDetails(user);
    setShowModal(true);
    setIsEdit(true);
  };
  const handleCloseResetModal = () => {
    setShowResetModal(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getAllUserList());
        await dispatch(getAllUserRole());
      } catch (error) {}
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    let result = [...getAllUser];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (user) =>
          (user?.USER_NAME &&
            user?.USER_NAME?.toLowerCase()?.includes(searchLower)) ||
          (user?.EMAIL && user?.EMAIL?.toLowerCase()?.includes(searchLower))
      );
    }

    if (statusFilter !== "All") {
      result = result.filter((user) => user?.ROLE_ID == statusFilter);
    }

    setFilteredUsers(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [getAllUser, searchTerm, statusFilter]);

  // Event handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleItemsPerPageChange = (e) => {
    setUsersPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page
  };

  const handleViewDetails = (user) => {
    navigate(`/AresaUserDetails`, {
      state: {
        id: user.id,
        userDetails: user,
      },
    });
  };

  // Pagination calculations
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Pagination handlers
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  // Get visible page numbers
  const getVisiblePageNumbers = () => {
    const delta = 1; // Number of pages to show before and after current page
    const range = [];
    const rangeWithDots = [];
    let l;

    // Always show first page
    range.push(1);

    // Calculate range of pages to show
    for (let i = currentPage - delta; i <= currentPage + delta; i++) {
      if (i > 1 && i < totalPages) {
        range.push(i);
      }
    }

    // Always show last page
    if (totalPages > 1) {
      range.push(totalPages);
    }

    // Add dots where needed
    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  const deleteEvent = async (user) => {
    if (
      window.confirm(
        "Are you sure you want to delete this User? This action cannot be undone."
      )
    ) {
      console.log(user);
      const res = await dispatch(
        handleDeleteUser(user?.ROLE_ID, user?.USER_ID)
      );
      if (res.rCode == 0) {
        alert(res.rMessage || "User Delete successfully!");
        await dispatch(getAllUserList());
      } else {
        alert(res.rMessage || "Error in Delete User!");
      }
    }
  };

  return (
    <div className="container-fluid">
      <DashboardHeader title="Registration List" />

      <div className="stats-grid">
        <StatCard
          title="Total Users"
          value={totalUser}
          icon={Icons.Users}
          color="#4A6CF7"
        />
      </div>

      {/* Filters and Search */}
      <ContentCard title="Search & Filters" icon={Icons.Filter}>
        <div className="modern-filters">
          <div className="search-box">
            <div className="search-icon">{Icons.Search}</div>
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, Email "
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                className="search-clear"
                onClick={() => {setSearchTerm("")}}
                aria-label="Clear search"
              >
                <i className="bi bi-x-circle-fill"></i>
              </button>
            )}
          </div>

          <div className="filter-controls">
            <div className="status-filter">
              <label>Role:</label>
              <select
                className="modern-select"
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="All">All Role</option>
                {userRoleMaster.map(
                  (role) =>
                    role.ROLE_ID != 1 &&
                    role.ROLE_ID != 4 && (
                      <option key={role.ROLE_ID} value={role.ROLE_ID}>
                        {role.ROLE_NAME}
                      </option>
                    )
                )}
              </select>
            </div>

            <div className="per-page-filter">
              <label>Show:</label>
              <select
                className="modern-select"
                value={usersPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value={5}>05</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>
        </div>
      </ContentCard>

      <ContentCard
        title="Users List"
        icon={Icons.User}
        action={
          <button
            className="modern-button"
            onClick={() => {
              openModal();
            }}
          >
            <i className="bi bi-plus-lg me-1"></i> Add Member
          </button>
        }
      >
        {loading ? (
          <Loader message="Loading users..." />
        ) : (
          <div className="users-container">
            <div className="modern-table-container">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th className="sortable-header">
                      <div className="header-content">
                        <span>User ID</span>
                      </div>
                    </th>
                    <th className="sortable-header">
                      <div className="header-content">
                        <span>Name</span>
                      </div>
                    </th>

                    <th className="sortable-header">
                      <div className="header-content">
                        <span>Email</span>
                      </div>
                    </th>
                    <th className="sortable-header">
                      <div className="header-content">
                        <span>Center Name</span>
                      </div>
                    </th>
                    <th className="sortable-header">
                      <div className="header-content">
                        <span>Entry Date</span>
                      </div>
                    </th>

                    <th>
                      <div className="header-content">
                        <span>Actions</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {currentUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-table">
                        <div className="empty-state">
                          <div className="empty-icon">
                            <i className="bi bi-person-x"></i>
                          </div>
                          <h4>No Users Found</h4>
                          <p>
                            {getAllUser.length === 0
                              ? "No users found in database"
                              : "No users match your filters"}
                          </p>
                          {searchTerm && (
                            <button
                              className="modern-button outlined"
                              onClick={() => setSearchTerm("")}
                            >
                              Clear Search
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentUsers.map(
                      (user) =>
                        user.ROLE_ID != 4 && (
                          <tr key={user.id} className="data-row">
                            <td>
                              <div className="user-id">
                                <span className="id-badge">
                                  U{user?.USER_ID.toString().padStart(5, "0")}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="user-info">
                                <div className="avatar">
                                  {user?.USER_NAME?.charAt(0)?.toUpperCase()}
                                </div>
                                <div className="user-data">
                                  <div className="user-name">
                                    {user?.USER_NAME || "N/A"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td>
                              <div className="contact-info">
                                {user?.EMAIL || "N/A"}
                              </div>
                            </td>

                            <td>
                              <div className="contact-info">
                                {user?.ROLE_NAME || "N/A"}
                              </div>
                            </td>
                            <td>
                              <div className="date-display">
                                {user?.ENTRY_DATETIME || "N/A"}
                              </div>
                            </td>

                            <td>
                              <button
                                className="btn btn-sm btn-outline-warning me-1"
                                title="Edit User"
                                onClick={() => {
                                  handleEditUser(user);
                                }}
                              >
                                <i className="bi bi-pencil-fill"></i>
                              </button>

                              <button
                                className="btn btn-sm btn-outline-info me-1"
                                title="Change Password"
                                onClick={() => {
                                  handleResetModal(user);
                                }}
                              >
                                <i className="bi bi-lock-fill"></i>
                              </button>

                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Delete"
                                onClick={() => {
                                  deleteEvent(user);
                                }}
                              >
                                <i className="bi bi-trash-fill"></i>
                              </button>
                            </td>
                          </tr>
                        )
                    )
                  )}
                </tbody>
              </table>
            </div>

            {filteredUsers.length > 0 && (
              <div className="modern-pagination">
                <div className="pagination-info">
                  Showing {indexOfFirstUser + 1} to{" "}
                  {Math.min(indexOfLastUser, filteredUsers.length)} of{" "}
                  {filteredUsers.length} entries
                </div>

                <div className="pagination-controls">
                  <button
                    className="page-button first"
                    onClick={() => paginate(1)}
                    disabled={currentPage === 1}
                  >
                    {Icons.FirstPage}
                  </button>

                  <button
                    className="page-button prev"
                    onClick={prevPage}
                    disabled={currentPage === 1}
                  >
                    {Icons.PrevPage}
                  </button>

                  <div className="page-numbers">
                    {getVisiblePageNumbers().map((page, index) =>
                      page === "..." ? (
                        <span key={`dots-${index}`} className="page-dots">
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          className={`page-button number ${
                            currentPage === page ? "active" : ""
                          }`}
                          onClick={() => paginate(page)}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    className="page-button next"
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                  >
                    {Icons.NextPage}
                  </button>

                  <button
                    className="page-button last"
                    onClick={() => paginate(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    {Icons.LastPage}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </ContentCard>
      <AddMember
        onClose={handleCloseModal}
        show={showModal}
        userDetails={userDetails}
        isEdit={isEdit}
      />
      <ResetPassword
        onClose={handleCloseResetModal}
        show={showResetModal}
        userDetails={userDetails}
      />
    </div>
  );
};

export default Registration;
